(ns practitest.frontend.forms3.run.events
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.link-entities-events :as link-events]
    [practitest.frontend.common-entities.metadata :as metadata]
    [practitest.frontend.components.grid.server :as grid-server]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.events :as events]
    [practitest.frontend.forms3.entity-form.forms :as f3.forms]
    [practitest.frontend.issue.bugtracker :as bugtracker]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.forms3.entity-form :as f3.entity-form]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; Timer

(re-frame/reg-event-fx
  ::fetch-duration
  (fn [{db :db} [_ run-id]]
    {:http-xhrio {:method          :get
                  :uri             (broutes/get-url "qtest_runs#get_duration"
                                                    {:p_id (subs/get-current-project-id db)
                                                     :id   run-id})
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::got-duration run-id]
                  :on-failure      [::fetch-navigation-failure]}}))

(re-frame/reg-event-fx
  ::got-duration
  (fn [{db :db} [_ run-id {:keys [duration_sec timer_paused run_completed]}]]
    (cond-> {:db (-> db
                     (assoc-in [:entity :run :timer :elapsed] duration_sec)
                     (assoc-in [:entity :run :timer :run-id] run-id))}
      run_completed                       (assoc :dispatch [::stop-timer])
      (not (or timer_paused run_completed)) (assoc :dispatch [::start-timer]))))

(re-frame/reg-event-db
  ::start-timer
  (fn [db _]
    (assoc-in db [:entity :run :timer :running?] true)))

(re-frame/reg-event-db
  ::stop-timer
  (fn [db _]
    (assoc-in db [:entity :run :timer :running?] false)))

(re-frame/reg-event-db
  ::set-elapsed
  (fn [db [_ seconds]]
    (assoc-in db [:entity :run :timer :elapsed] seconds)))

(re-frame/reg-event-fx
  ::save-duration
  (fn [{db :db} [_ opts]]
    (let [run-id  (get-in db [:entity :run :timer :run-id])
          elapsed (or (:elapsed opts) (get-in db [:entity :run :timer :elapsed] 0))
          paused? (:paused? opts)]
      {:http-xhrio {:method          :post
                    :uri             (broutes/get-url "qtest_runs#update_duration"
                                                      {:p_id (subs/get-current-project-id db)
                                                       :id   run-id})
                    :format          (ajax/json-request-format)
                    :params          (cond-> {:duration_in_sec elapsed}
                                       (some? paused?) (assoc :timer_paused paused?))
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::save-duration-success]
                    :on-failure      [::fetch-navigation-failure]}})))

(re-frame/reg-event-db
  ::save-duration-success
  (fn [db _] db))

;; Run

(def ^:private entity-type :run)
(def ^:private metadata (get metadata/entities-metadata entity-type))
(def ^:private form-id (get metadata :form-id))

(re-frame/reg-event-fx
  ::got-run
  (fn [{db :db} [_ _ response]]
    (let [run-id     (get-in response [:entity :id])
          testset-id (get-in response [:testset :id])]
      {:db       (-> db
                     (assoc-in [:entity :run :parent-instance] (:instance response))
                     (assoc-in [:entity :run :parent-testset] (:testset response))
                     (assoc-in [:entity :run :parent-test] (:test response)))
       :dispatch [::fetch-navigation run-id testset-id]})))

(re-frame/reg-event-db
  ::reset-run-form
  (fn [db [_ new?]]
    (f3.forms/reset-form-state db form-id new? entity-type)))

(re-frame/reg-event-fx
  ::fetch-navigation
  (fn [{db :db} [_ run-id testset-id]]
    (let [project-id  (subs/get-current-project-id db)
          table-id    (str project-id "-testset2-instances-grid-" testset-id)
          autofilters (grid-session-storage/load-autofilters table-id)
          sort-order  (grid-session-storage/load-sort-order table-id)
          gridfilter  (when (seq autofilters)
                        (grid-server/autofilters->json autofilters))
          order-by    (when sort-order (name (first sort-order)))
          order-dir   (when sort-order (name (second sort-order)))]
      {:db         (assoc-in db [:entity :run :navigation :loading?] true)
       :http-xhrio {:method          :get
                    :uri             (broutes/get-url "qtest_runs#navigation"
                                                      {:p_id project-id
                                                       :id   run-id})
                    :params          (cond-> {}
                                       gridfilter (assoc :gridfilter gridfilter)
                                       order-by   (assoc :order_by order-by
                                                         :order_direction order-dir))
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::got-navigation]
                    :on-failure      [::events/api-error ::fetch-navigation-failure {}]}})))

(re-frame/reg-event-db
  ::got-navigation
  (fn [db [_ response]]
    (assoc-in db [:entity :run :navigation] (assoc response :loading? false))))

(re-frame/reg-event-db
  ::fetch-navigation-failure
  (fn [db _]
    (assoc-in db [:entity :run :navigation :loading?] false)))

(re-frame/reg-event-fx
  ::navigate-to-instance-success
  (fn [_ [_ response]]
    (if (seq (:errors response))
      {:dispatch [::notifications/error2 (first (:errors response))]}
      {::events/redirect-delayed (:redirect response)})))

(re-frame/reg-event-fx
  ::navigate-to-instance-failure
  (fn [_ _]
    {:dispatch [::notifications/error2 (get-translation "run.navigation-error" "Error navigating to run")]}))

(re-frame/reg-event-fx
  ::stop-and-navigate-to-instance
  (fn [_ [_ instance-id testset-id]]
    {:dispatch-n [[::stop-timer]
                  [::navigate-to-instance instance-id testset-id]]}))

(re-frame/reg-event-fx
  ::stop-and-navigate-to-route
  (fn [_ [_ route-name path-params]]
    {:dispatch-n [[::stop-timer]
                  [::router/push-state route-name path-params]]}))

(re-frame/reg-event-fx
  ::stop-and-navigate-back
  (fn [_ _]
    ;; router.cljs's popstate guard independently blocks back-nav on a dirty
    ;; form and reloads instead - unregister first so it doesn't re-block a
    ;; navigation already confirmed via the Unsynced Changes modal. Keyword
    ;; must be fully-qualified since run.cljs registered it in its own ns.
    (router/remove-dirty-fields! :practitest.frontend.forms3.run/run-steps-dirty)
    (js/history.back)
    {}))

(re-frame/reg-event-fx
  ::safe-navigate-to-instance
  (fn [{db :db} [_ instance-id testset-id]]
    (if (get-in db [:entity :run :steps :has-non-called-dirty?])
      {:dispatch [::show-unsynced-warning [::stop-and-navigate-to-instance instance-id testset-id]]}
      {:dispatch [::stop-and-navigate-to-instance instance-id testset-id]})))

(re-frame/reg-event-fx
  ::safe-navigate-to-route
  (fn [{db :db} [_ route-name path-params]]
    (if (get-in db [:entity :run :steps :has-non-called-dirty?])
      {:dispatch [::show-unsynced-warning [::stop-and-navigate-to-route route-name path-params]]}
      {:dispatch [::stop-and-navigate-to-route route-name path-params]})))

(re-frame/reg-event-fx
  ::safe-navigate-back
  (fn [{db :db} _]
    (if (get-in db [:entity :run :steps :has-non-called-dirty?])
      {:dispatch [::show-unsynced-warning [::stop-and-navigate-back]]}
      {:dispatch [::stop-and-navigate-back]})))

(re-frame/reg-event-fx
  ::navigate-to-instance
  (fn [{db :db} [_ instance-id testset-id]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "instances#run_now"
                                                    {:p_id   (subs/get-current-project-id db)
                                                     :set_id testset-id
                                                     :id     instance-id})
                  :format          (ajax/json-request-format)
                  :params          {:run_from_instance "true"}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::navigate-to-instance-success]
                  :on-failure      [::navigate-to-instance-failure]}}))

(re-frame/reg-event-fx
  ::save-run
  (fn [{db :db} [_ _run-id {:keys [new?] :as options}]]
    {:dispatch [::common-events/save-entity form-id entity-type
                (assoc options
                       :redirect-key :redirect
                       :got-entity-data [::got-run])]}))

(re-frame/reg-event-fx
  ::refresh-run
  (fn [{db :db} [_ run-id]]
    {:dispatch [::f3.entity-form/fetch-entity
                {:form-id         form-id
                 :entity-type     entity-type
                 :entity-id       run-id
                 :project-id      (subs/get-current-project-id db)
                 :new?            false
                 :got-entity-data [::got-run]}]}))

;; Steps

(re-frame/reg-event-fx
  ::fetch-steps
  (fn [{db :db} [_ run-id]]
    {:db         (assoc-in db [:entity :run :steps :loading?] true)
     :http-xhrio {:method          :get
                  :uri             (broutes/get-url "qtest_runs#steps"
                                                    {:p_id (subs/get-current-project-id db)
                                                     :id   run-id})
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::got-steps]
                  :on-failure      [::events/api-error ::fetch-steps-failure {}]}}))

(re-frame/reg-event-db
  ::got-steps
  (fn [db [_ {:keys [preconditions step_runs]}]]
    (let [step-runs (:data step_runs)
          db' (-> db
                  (assoc-in [:entity :run :steps :preconditions] preconditions)
                  (assoc-in [:entity :run :steps :step-runs] step-runs)
                  (assoc-in [:entity :run :steps :loading?] false))]
      (reduce (fn [d step-run]
                (let [id               (:id step-run)
                      run-attachments  (get-in step-run [:attributes :run-attachments])
                      design-attachments (get-in step-run [:attributes :design-attachments])]
                  (cond-> d
                    (seq run-attachments)
                    (assoc-in [:entity :step-run id :attachments :data] run-attachments)
                    (seq design-attachments)
                    (assoc-in [:entity :step-design id :attachments :data] design-attachments))))
              db'
              step-runs))))

(re-frame/reg-event-db
  ::fetch-steps-failure
  (fn [db _]
    (assoc-in db [:entity :run :steps :loading?] false)))


(re-frame/reg-event-fx
  ::pass-all
  (fn [{db :db} [_ run-id]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "step_runs#pass_all_steps"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         run-id})
                  :format          (ajax/json-request-format)
                  :response-format (ajax/raw-response-format)
                  :on-success      [::pass-all-success run-id]
                  :on-failure      [::events/api-error ::pass-all-failure {}]}}))

(re-frame/reg-event-fx
  ::pass-all-success
  (fn [_ [_ run-id _response]]
    {:dispatch-n [[::notifications/success2
                   (get-translation "run.steps.passed-all" "All steps passed")]
                  [::refresh-run run-id]
                  [::fetch-steps run-id]
                  [::fetch-duration run-id]]}))

(re-frame/reg-event-fx
  ::pass-all-failure
  (fn [_ _]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.pass-all-error" "Error passing all steps")]}))

;; Step editing state - tracked per-step (see run/subs.cljs ::step-editing?)
;; so that editing/saving one step can't clobber another step's edit state.
;; Drafts live in the db (not local component state) so that "Update Test" can
;; read and flush any in-progress, unsaved edits before syncing to the test.

(re-frame/reg-event-db
  ::start-step-edit
  (fn [db [_ step-id draft]]
    (-> db
        (update-in [:entity :run :steps :editing-step-ids] (fnil conj #{}) step-id)
        (assoc-in [:entity :run :steps :drafts step-id] draft))))

(re-frame/reg-event-db
  ::update-step-draft
  (fn [db [_ step-id field value]]
    (assoc-in db [:entity :run :steps :drafts step-id field] value)))

(re-frame/reg-event-db
  ::clear-step-editing
  (fn [db [_ step-id]]
    (-> db
        (update-in [:entity :run :steps :editing-step-ids] (fnil disj #{}) step-id)
        (update-in [:entity :run :steps :drafts] dissoc step-id))))

(re-frame/reg-event-db
  ::clear-all-step-editing
  (fn [db _]
    (-> db
        (assoc-in [:entity :run :steps :editing-step-ids] #{})
        (assoc-in [:entity :run :steps :drafts] {}))))

;; Unsynced warning modal

(re-frame/reg-event-db
  ::show-unsynced-warning
  (fn [db [_ pending-event]]
    (assoc-in db [:entity :run :unsynced-warning] {:show? true :pending-event pending-event})))

(re-frame/reg-event-db
  ::hide-unsynced-warning
  (fn [db _]
    (update-in db [:entity :run] dissoc :unsynced-warning)))

(re-frame/reg-event-fx
  ::confirm-navigate
  (fn [{db :db} _]
    (let [pending (get-in db [:entity :run :unsynced-warning :pending-event])]
      {:db       (update-in db [:entity :run] dissoc :unsynced-warning)
       :dispatch pending})))

;; Any step still open for editing has an unsaved draft that never reached the
;; backend (per-step Save is what actually persists it). "Update Test" re-syncs
;; the parent test from whatever is already persisted, so those steps must be
;; saved first, or their edits would silently be dropped from the sync.
(re-frame/reg-event-fx
  ::flush-then-sync
  (fn [{db :db} [_ run-id]]
    (let [editing-ids (get-in db [:entity :run :steps :editing-step-ids] #{})]
      (if (empty? editing-ids)
        {:dispatch [::sync-to-test run-id]}
        {:db         (assoc-in db [:entity :run :steps :sync-after-flush] run-id)
         :dispatch-n (mapv (fn [step-id]
                             (let [draft (get-in db [:entity :run :steps :drafts step-id])]
                               [::update-step step-id
                                {:name             (:name draft)
                                 :description      (:description draft)
                                 :expected_results (:expected-results draft)}]))
                           editing-ids)}))))

(re-frame/reg-event-fx
  ::sync-to-test
  (fn [{db :db} [_ run-id]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "qtest_runs#update_parent_test_steps"
                                                    {:p_id (subs/get-current-project-id db)
                                                     :id   run-id})
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::sync-success]
                  :on-failure      [::events/api-error ::sync-failure {}]}}))

(re-frame/reg-event-fx
  ::sync-success
  (fn [{db :db} _]
    (let [pending (get-in db [:entity :run :unsynced-warning :pending-event])]
      {:db         (-> db
                       (assoc-in [:entity :run :steps :dirty?] false)
                       (assoc-in [:entity :run :steps :has-non-called-dirty?] false)
                       (update-in [:entity :run] dissoc :unsynced-warning))
       :dispatch-n (cond-> [[::notifications/success2
                              (get-translation "run.steps.sync-success" "Original test details updated")]]
                     pending (conj pending))})))

(re-frame/reg-event-fx
  ::sync-failure
  (fn [_ _]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.sync-error" "Error updating original test details")]}))

(re-frame/reg-event-fx
  ::update-step-status
  (fn [{db :db} [_ step-id run-id new-status]]
    {:db         (update-in db [:entity :run :steps :step-runs]
                            (fn [steps]
                              (map (fn [step]
                                     (if (= (:id step) step-id)
                                       (assoc-in step [:attributes :status] new-status)
                                       step))
                                   steps)))
     :http-xhrio {:method          :post
                  :uri             (broutes/get-url "step_runs#update_status"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :format          (ajax/json-request-format)
                  :params          {:status new-status}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::update-step-status-success run-id]
                  :on-failure      [::update-step-status-failure run-id]}}))

(re-frame/reg-event-fx
  ::update-step-status-success
  (fn [_ [_ run-id]]
    {:dispatch-n [[::refresh-run run-id]
                  [::fetch-duration run-id]]}))

(re-frame/reg-event-fx
  ::update-step-status-failure
  (fn [_ [_ run-id]]
    {:dispatch-n [[::notifications/error2
                   (get-translation "run.steps.update-status-error" "Error updating step status")]
                  [::fetch-steps run-id]]}))

(re-frame/reg-event-fx
  ::report-bugtracker-issue
  (fn [{db :db} [_ step-id]]
    {:http-xhrio {:method          :get
                  :uri             (broutes/get-url "step_runs#bug_defaults"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::got-bugtracker-issue-details step-id]
                  :on-failure      [::got-bugtracker-issue-details-failure step-id]}}))

(re-frame/reg-event-fx
  ::got-bugtracker-issue-details
  (fn [_ [_ step-id {:keys [name description]}]]
    {:dispatch [::bugtracker/show-bugtracker-modal! :create
                {:issue-name        name
                 :issue-description description
                 :params            {:step_run_id step-id
                                     :entity_type nil
                                     :create_only? true}
                 :on-success-events [[::refresh-step-linked-issues step-id]]}]}))

(re-frame/reg-event-fx
  ::got-bugtracker-issue-details-failure
  (fn [_ [_ step-id]]
    {:dispatch [::bugtracker/show-bugtracker-modal! :create
                {:params            {:step_run_id step-id
                                     :entity_type nil
                                     :create_only? true}
                 :on-success-events [[::refresh-step-linked-issues step-id]]}]}))

(re-frame/reg-event-db
  ::update-step-linked-issues
  (fn [db [_ {:keys [id linked-issues]}]]
    (update-in db [:entity :run :steps :step-runs]
               (fn [steps]
                 (map (fn [existing-step]
                        (if (= (str (:id existing-step)) (str id))
                          (assoc-in existing-step [:attributes :linked-issues] linked-issues)
                          existing-step))
                      steps)))))

(re-frame/reg-event-fx
  ::link-existing-issue
  (fn [{db :db} [_ step-id issue-display-id]]
    {:db         (update db :progress conj ::link-existing-issue)
     :http-xhrio {:method          :post
                  :uri             (broutes/get-url "issues_step_runs#create"
                                                    {:project_id (subs/get-current-project-id db)})
                  :format          (ajax/json-request-format)
                  :params          {:step_id          step-id
                                    :issue_display_id issue-display-id}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::link-existing-issue-success]
                  :on-failure      [::link-existing-issue-failure]}}))

(re-frame/reg-event-fx
  ::link-existing-issue-success
  (fn [{db :db} [_ response]]
    {:db         (assoc-in db [:entity :run :steps :link-issue-by-id-error] nil)
     :dispatch-n [[::notifications/success2
                   (get-translation "run.steps.link-issue-success" "Issue linked successfully")]
                  [::update-step-linked-issues (:step response)]
                  [::hide-link-issue-by-id-modal]]}))

(re-frame/reg-event-fx
  ::refresh-step-linked-issues
  (fn [{db :db} [_ step-id]]
    {:http-xhrio {:method          :get
                  :uri             (broutes/get-url "issues_step_runs#linked_issues"
                                                    {:project_id (subs/get-current-project-id db)})
                  :params          {:step_id step-id}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::refresh-step-linked-issues-success]
                  :on-failure      [::refresh-step-linked-issues-failure]}}))

(re-frame/reg-event-fx
  ::refresh-step-linked-issues-success
  (fn [_ [_ response]]
    {:dispatch [::update-step-linked-issues (:step response)]}))

(re-frame/reg-event-fx
  ::refresh-step-linked-issues-failure
  (fn [_ _]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.link-issue-error" "Error linking issue")]}))

(re-frame/reg-event-db
  ::link-existing-issue-failure
  (fn [db [_ response]]
    (-> db
        (update :progress disj ::link-existing-issue)
        (assoc-in [:entity :run :steps :link-issue-by-id-error]
                  (or (first (get-in response [:response :errors]))
                      (get-translation "run.steps.link-issue-error" "Error linking issue"))))))

(re-frame/reg-event-fx
  ::unlink-issue
  (fn [{db :db} [_ issues-step-run-id]]
    {:db         (update db :progress conj ::unlink-issue)
     :http-xhrio {:method          :delete
                  :uri             (broutes/get-url "issues_step_runs#destroy"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         issues-step-run-id})
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::unlink-issue-success]
                  :on-failure      [::unlink-issue-failure]}}))

(re-frame/reg-event-fx
  ::unlink-issue-success
  (fn [_ [_ response]]
    {:dispatch-n [[::notifications/success2
                   (get-translation "run.steps.unlink-issue-success" "Issue unlinked successfully")]
                  [::update-step-linked-issues (:step response)]
                  [::hide-unlink-confirm-modal]]}))

(re-frame/reg-event-fx
  ::unlink-issue-failure
  (fn [{db :db} _]
    {:db       (update db :progress disj ::unlink-issue)
     :dispatch [::notifications/error2
                (get-translation "run.steps.unlink-issue-error" "Error unlinking issue")]}))

(re-frame/reg-event-db
  ::show-unlink-confirm-modal
  (fn [db [_ issues-step-run-id]]
    (assoc-in db [:entity :run :steps :unlink-confirm-target-id] issues-step-run-id)))

(re-frame/reg-event-db
  ::hide-unlink-confirm-modal
  (fn [db _]
    (-> db
        (assoc-in [:entity :run :steps :unlink-confirm-target-id] nil)
        (update :progress disj ::unlink-issue))))

(re-frame/reg-event-db
  ::set-link-issues-target-step
  (fn [db [_ step-id]]
    (assoc-in db [:entity :run :steps :link-issues-target-step] step-id)))

(re-frame/reg-event-fx
  ::hide-link-issues-dialog
  (fn [_ [_ state-key]]
    {:dispatch-n [[::link-events/hide-link-entities-modal state-key]
                  [::set-link-issues-target-step nil]]}))

(re-frame/reg-event-db
  ::show-link-issue-by-id-modal
  (fn [db _]
    (-> db
        (assoc-in [:entity :run :steps :link-issue-by-id-modal-open?] true)
        (assoc-in [:entity :run :steps :link-issue-by-id-error] nil))))

(re-frame/reg-event-db
  ::hide-link-issue-by-id-modal
  (fn [db _]
    (-> db
            (assoc-in [:entity :run :steps :link-issue-by-id-modal-open?] false)
            (assoc-in [:entity :run :steps :link-issue-by-id-error] nil)
            (assoc-in [:entity :run :steps :link-issues-target-step] nil)
            (update :progress disj ::link-existing-issue))))

(re-frame/reg-event-db
  ::clear-link-issue-by-id-error
  (fn [db _]
    (assoc-in db [:entity :run :steps :link-issue-by-id-error] nil)))

(re-frame/reg-event-fx
  ::link-selected-issues-to-step
  (fn [{db :db} [_ _grid-entity state-key selected-ids]]
    (let [step-id     (get-in db [:entity :run :steps :link-issues-target-step])
          rows        (get-in db [:entity :run state-key :data])
          display-ids (->> rows
                           (filter #(contains? selected-ids (:id %)))
                           (map :display_id)
                           (str/join ","))]
      {:db         (update db :progress conj ::link-selected-issues-to-step)
       :http-xhrio {:method          :post
                    :uri             (broutes/get-url "issues_step_runs#create"
                                                      {:project_id (subs/get-current-project-id db)})
                    :format          (ajax/json-request-format)
                    :params          {:step_id          step-id
                                      :issue_display_id display-ids}
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::link-selected-issues-to-step-success state-key]
                    :on-failure      [::link-selected-issues-to-step-failure]}})))

(re-frame/reg-event-fx
  ::link-selected-issues-to-step-success
  (fn [{db :db} [_ state-key response]]
    {:db         (update db :progress disj ::link-selected-issues-to-step)
     :dispatch-n [[::notifications/success2
                   (get-translation "run.steps.link-issue-success" "Issue linked successfully")]
                  [::update-step-linked-issues (:step response)]
                  [::common-events/toggle-link-entities-modal :issue state-key
                   ::common-events/fetch-entity-list nil]
                  [::set-link-issues-target-step nil]]}))

(re-frame/reg-event-fx
  ::link-selected-issues-to-step-failure
  (fn [{db :db} [_ response]]
    {:db       (update db :progress disj ::link-selected-issues-to-step)
     :dispatch [::notifications/error2
                (or (first (get-in response [:response :errors]))
                    (get-translation "run.steps.link-issue-error" "Error linking issue"))]}))

(re-frame/reg-event-fx
  ::update-step-actual-results
  (fn [{db :db} [_ step-id value]]
    {:db         (update-in db [:entity :run :steps :step-runs]
                            (fn [steps]
                              (map (fn [step]
                                     (if (= (:id step) step-id)
                                       (assoc-in step [:attributes :actual-results] value)
                                       step))
                                   steps)))
     :http-xhrio {:method          :post
                  :uri             (broutes/get-url "step_runs#update_actual_results"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :format          (ajax/json-request-format)
                  :params          {:value value}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::update-step-actual-results-success]
                  :on-failure      [::update-step-actual-results-failure step-id]}}))

(re-frame/reg-event-db
  ::update-step-actual-results-success
  (fn [db _] db))

(re-frame/reg-event-fx
  ::update-step-actual-results-failure
  (fn [_ [_ step-id]]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.update-actual-results-error" "Error saving actual results")]}))

(re-frame/reg-event-db
  ::set-pending-edit-step
  (fn [db [_ step-id]]
    (assoc-in db [:entity :run :steps :pending-edit-id] step-id)))

(re-frame/reg-event-db
  ::clear-pending-edit-step
  (fn [db _]
    (update-in db [:entity :run :steps] dissoc :pending-edit-id)))

(re-frame/reg-event-fx
  ::add-step-created
  (fn [{db :db} [_ run-id response]]
    {:db (-> db
             (assoc-in [:entity :run :steps :dirty?] true)
             (assoc-in [:entity :run :steps :has-non-called-dirty?] true))
     :dispatch-n [[::set-pending-edit-step (str (:id response))]
                  [::fetch-steps run-id]]}))

(re-frame/reg-event-fx
  ::step-mutated
  (fn [{db :db} [_ run-id message]]
    {:db (assoc-in db [:entity :run :steps :dirty?] true)
     :dispatch-n [[::fetch-steps run-id]
                  [::notifications/success2 message]]}))

(re-frame/reg-event-fx
  ::add-step
  (fn [{db :db} [_ step-id run-id above?]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "step_runs#create_new"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :format          (ajax/json-request-format)
                  :params          {:b_above above?}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::add-step-created run-id]
                  :on-failure      [::events/api-error ::add-step-failure {}]}}))

(re-frame/reg-event-fx
  ::add-step-failure
  (fn [_ _]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.add-step-error" "Error adding step")]}))

(re-frame/reg-event-fx
  ::delete-step-success
  (fn [{db :db} [_ step-id run-id]]
    (let [steps   (get-in db [:entity :run :steps :step-runs])
          step    (some #(when (= (:id %) step-id) %) steps)
          called? (boolean (get-in step [:attributes :call-to-test-id]))]
      {:db (cond-> (assoc-in db [:entity :run :steps :dirty?] true)
             (not called?) (assoc-in [:entity :run :steps :has-non-called-dirty?] true))
       :dispatch-n [[::fetch-steps run-id]
                    [::notifications/success2 (get-translation "run.steps.delete-step-success" "Step deleted")]]})))

(re-frame/reg-event-fx
  ::delete-step
  (fn [{db :db} [_ step-id run-id]]
    {:http-xhrio {:method          :delete
                  :uri             (broutes/get-url "step_runs#destroy"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::delete-step-success step-id run-id]
                  :on-failure      [::events/api-error ::delete-step-failure {}]}}))

(re-frame/reg-event-fx
  ::delete-step-failure
  (fn [_ _]
    {:dispatch [::notifications/error2
                (get-translation "run.steps.delete-step-error" "Error deleting step")]}))

(re-frame/reg-event-fx
  ::update-step
  (fn [{db :db} [_ step-id attrs]]
    {:db          (update-in db [:entity :run :steps :saving-step-ids] (fnil conj #{}) step-id)
     :http-xhrio {:method          :put
                  :uri             (broutes/get-url "step_runs#update"
                                                    {:project_id (subs/get-current-project-id db)
                                                     :id         step-id})
                  :format          (ajax/json-request-format)
                  :params          {:step_run attrs}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::update-step-success step-id]
                  :on-failure      [::update-step-failure step-id]}}))

;; step_runs#update's response bypasses the configured :json_api adapter
;; (StepRunSerializer.new(@step).as_json uses ActiveModelSerializers' plain
;; Attributes adapter, not the app's json_api one), so it comes back flat
;; with underscored keys
(defn- update-step-response->attributes [response]
  {:name              (:name response)
   :description       (:description response)
   :expected-results  (:expected_results response)
   :actual-results    (:actual_results response)
   :status            (:status response)
   :position          (:position response)
   :call-to-test-id   (:call_to_test_id response)})

(re-frame/reg-event-fx
  ::update-step-success
  (fn [{db :db} [_ step-id response]]
    (let [called?     (boolean (:call_to_test_id response))
          ;; This save is part of a flush-then-sync batch, not a standalone
          ;; per-step Save click - skip its own toast, ::sync-to-test's
          ;; "Original test details updated" toast below covers it instead.
          flushing?   (some? (get-in db [:entity :run :steps :sync-after-flush]))
          db'         (-> db
                          (assoc-in [:entity :run :steps :dirty?] true)
                          (cond-> (not called?) (assoc-in [:entity :run :steps :has-non-called-dirty?] true))
                          (update-in [:entity :run :steps :saving-step-ids] (fnil disj #{}) step-id)
                          (update-in [:entity :run :steps :editing-step-ids] (fnil disj #{}) step-id)
                          (update-in [:entity :run :steps :drafts] dissoc step-id)
                          (update-in [:entity :run :steps :step-runs]
                                     (fn [step-runs]
                                       (mapv (fn [step]
                                               (if (= (:id step) step-id)
                                                 (update step :attributes merge (update-step-response->attributes response))
                                                 step))
                                             step-runs))))
          sync-run-id (get-in db' [:entity :run :steps :sync-after-flush])
          flush-done? (and sync-run-id
                           (empty? (get-in db' [:entity :run :steps :editing-step-ids]))
                           (empty? (get-in db' [:entity :run :steps :saving-step-ids])))]
      {:db       (cond-> db' flush-done? (update-in [:entity :run :steps] dissoc :sync-after-flush))
       :dispatch-n (cond-> []
                     (not flushing?) (conj [::notifications/success2 (get-translation "run.steps.update-step-success" "Step updated")])
                     flush-done? (conj [::sync-to-test sync-run-id]))})))

(re-frame/reg-event-fx
  ::update-step-failure
  (fn [{db :db} [_ step-id _error]]
    ;; Only clear the in-flight "saving" indicator, not editing-step-ids - the
    ;; row should stay in edit mode with the user's draft intact so they can
    ;; retry, rather than silently discarding it.
    (let [pending-sync? (some? (get-in db [:entity :run :steps :sync-after-flush]))]
      {:db       (-> db
                    (update-in [:entity :run :steps :saving-step-ids] (fnil disj #{}) step-id)
                    (cond-> pending-sync? (update-in [:entity :run :steps] dissoc :sync-after-flush)))
       :dispatch [::notifications/error2
                  (if pending-sync?
                    (get-translation "run.steps.update-step-error-sync-aborted"
                                     "Error saving a step - Update Test was not applied")
                    (get-translation "run.steps.update-step-error" "Error updating step"))]})))
